import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { Alert, Button, Option, Select, ThemeProvider, Typography } from '@material-tailwind/react';
import Datepicker from 'react-tailwindcss-datepicker';
import { CustomerData, CustomerDataSearchParams, OrderSource, ProducerLocation } from '../../types';
import { getDateInputValue } from '../lib/date';
import { formatDateLong } from '../lib/formatters';
import { formatMoney } from '../lib/money';
import formatPhoneNumber from '../lib/phone';
import { Pagination } from './Pagination';

const theme = {
	input: {
		styles: {
			base: {
				container: {
					minWidth: 'min-w-[50px]',
				},
			},
		},
	},
};

const DEFAULT_DATE_BOUNDS = {
	max: getDateInputValue(0), // today
};

interface PaginationProps {
	activePage: number;
	setActivePage: (activePage: number) => void;
    pageSize: number;
    setPageSize: (pageSize: number) => void;
}

interface SortProps {
	sortField: string;
	setSortField: (sortField: string) => void;
	sortOrder: string;
	setSortOrder: (sortOrder: string) => void;
}

interface Props {
	customerData: CustomerData[];
	customerDataLoaded: boolean;
	setCustomerDataSearchParams: (value: CustomerDataSearchParams) => void;
	customerDataSearchParams: CustomerDataSearchParams;
	paginationProps: PaginationProps;
	producerLocations?: ProducerLocation[] | null;
	orderSources?: OrderSource[] | null;
    searchAndDownloadCSV: () => void;
	sortProps: SortProps;
}

const CustomerDataTable = ({
	customerData,
	customerDataLoaded,
	customerDataSearchParams,
	setCustomerDataSearchParams,
	paginationProps,
	producerLocations,
	orderSources,
    searchAndDownloadCSV,
	sortProps,
}: Props) => {
	// const { date, pageIndex, transactionStatus, sortDirection } = invoiceSearchParams || {};

	// const invoicesHavePLKs = invoices.some((invoice) => invoice.producerLocationKey);
	// const producerLocationDropdownOptionsExist = producerLocations && !_.isEmpty(producerLocations);
	// const locationColumnExists = invoicesHavePLKs || producerLocationDropdownOptionsExist;

	const headers = [
		{
			label: 'Name',
		},
		{
			label: 'Phone',
		},
		{
			label: 'Email',
		},
		{
			label: 'Address',
		},
		{
			label: 'City & State',
		},
		{
			label: 'Zip',
		},
		{
			label: 'Order Count',
			fieldName: 'totalOrderCount',
			rowValueName: 'totalOrderCount',
		},
		{
			label: 'Average Order Subtotal',
			fieldName: 'averageOrderSubtotal',
			rowValueName: 'averageOrderSubtotal',
		},
		{
			label: 'Aggregate Order Subtotal',
			fieldName: 'aggregateOrderSubtotal',
			rowValueName: 'aggregateOrderSubtotal',
		},
		{
			label: 'First Order In Range',
		},
		{
			label: 'Last Order In Range',
		},
	].filter(Boolean);

	const { startDate = null, endDate = null, producerLocationKey } = customerDataSearchParams;
	const { sortField, setSortField, sortOrder, setSortOrder } = sortProps;

	return (
		<ThemeProvider value={theme}>
			<div className="flex justify-between items-center p-3 h-20">
				<h5 className="text-xl font-semibold">Customers</h5>
				<div className="w-[70%] px-6 flex items-end justify-between gap-4">
					<Datepicker
						value={{ startDate, endDate }}
						onChange={(dateBounds) => {
							if (dateBounds) {
								const { startDate, endDate }: {startDate: Date | string | null, endDate: Date | string | null} = dateBounds;
                                const searchParams = { ...customerDataSearchParams, startDate, endDate };
								setCustomerDataSearchParams(searchParams);
							}
						}}
						placeholder="Enter desired date range"
						maxDate={DEFAULT_DATE_BOUNDS.max}
					/>
					{producerLocations && (producerLocations.length > 1) && (
						<Select
							label="Location"
							onChange={(producerLocationKey) => {
								setCustomerDataSearchParams({
									...customerDataSearchParams,
									producerLocationKey,
								});
							}}
						>
							{[<Option value={undefined}>All</Option>].concat(
								producerLocations.map(({ producerLocationKey, producerLocationName }) => (
									<Option value={producerLocationKey}>{producerLocationName}</Option>
								))
							)}
						</Select>
					)}
					{orderSources && (
						<Select
							label="Order Source"
							onChange={(orderSourceName) => {
								setCustomerDataSearchParams({
									...customerDataSearchParams,
									orderSource: orderSourceName,
								});
							}}
						>
							{[<Option value={undefined}>All</Option>].concat(
								orderSources.map(({ orderSourceName }) => (
									<Option value={String(orderSourceName)}>{orderSourceName}</Option>
								))
							)}
						</Select>
					)}
					{/* <Button
						className="w-[20%]"
						onClick={() => {
							// setCustomerDataSearchParams({});
							// setSearchButtonDisabled(true);
						}}
						// disabled={searchButtonDisabled}
					>
						Search!
					</Button> */}
                    <Button onClick={searchAndDownloadCSV} 
                        disabled={!customerData || customerData.length === 0}
                    >
                        DOWNLOAD
                    </Button>
				</div>
			</div>
			<div className="flex-col max-h-[calc(100vh-10rem)] items-center">
				<table className="border mt-4 w-full text-left">
					<TableHeaders headers={headers} sortProps={sortProps} />
					<TableRows
						rows={customerData}
						invoicesLoaded={customerDataLoaded}
						headers={headers}
						// locationColumnExists={locationColumnExists}
					/>
				</table>
				{paginationProps && (paginationProps.activePage !== 1 || customerData.length >= 10) && (
					<Pagination activePage={paginationProps.activePage} setActivePage={paginationProps.setActivePage} pageSize={paginationProps.pageSize} setPageSize={paginationProps.setPageSize}/>
				)}
			</div>
		</ThemeProvider>
	);
};

const TableHeaders = ({ headers, sortProps }) => {
	const { setSortField, sortField, sortOrder, setSortOrder } = sortProps;

	const changeSortDirection = ({ mode }) => {
		if (mode === 'toggle') {
			setSortOrder(sortOrder === 'DESC' ? 'ASC' : 'DESC');
		}
		if (mode === 'reset') {
			setSortOrder('DESC');
		}
	};

	return (
		<thead>
			<tr>
				{headers.map((head, index) => (
					<th
						key={head.label}
						className={`cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-4 transition-colors hover:bg-blue-gray-50 ${
							head.width || ''
						}`}
						colSpan={head.colSpan || 1}
					>
						<div className="flex items-center justify-between gap-2 font-normal leading-none">
							<div className="w-full gap-6 opacity-70">
								<Typography variant="small" color="blue-gray" className="font-[420]">
									{head.label}
								</Typography>
							</div>
							{head.fieldName && 
								<ChevronUpDownIcon
									strokeWidth={2}
									className="h-4 w-4"
									onClick={() => {
										setSortField(head.fieldName);
										const mode =
											head.fieldName === sortField
												? 'toggle'
												: 'reset';
										changeSortDirection({ mode });
									}}
								/>
							}
						</div>
					</th>
				))}
			</tr>
		</thead>
	);
};

const TableRows = ({ rows, invoicesLoaded, headers }) => {

	return (
		<tbody>
			{!invoicesLoaded && rows.length === 0 && (
				<tr className="border-b-0">
					<td colSpan={headers.map((h) => h.colSpan || 1).reduce((a, b) => a + b)} className="p-8 py-4">
						<Alert color="orange" variant="ghost">
							Loading...
						</Alert>
					</td>
				</tr>
			)}
			{rows.map((consumer, index) => {
				const {
					consumerId,
					consumerName,
					consumerPhone,
					consumerEmail,
					consumerStreetNumber,
					consumerStreet,
					consumerCity,
					consumerState,
					consumerZip,
					totalOrderCount,
					averageOrderSubtotal,
					aggregateOrderSubtotal,
					firstOrderInRange,
					lastOrderInRange,
				} = consumer;

				const isLast = index === rows.length - 1;
				const classes = isLast ? 'p-4' : 'p-4 border-b border-blue-gray-50';

				return (
					<tr
						key={consumerId + `${index}`}
						className={
							invoicesLoaded ? 'cursor-pointer hover:bg-gray-100' : 'pointer-events-none bg-gray-100'
						}
						// onClick={() => window.open(url)}
					>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{consumerName}
							</Typography>
						</td>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{formatPhoneNumber(consumerPhone)}
							</Typography>
						</td>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{consumerEmail}
							</Typography>
						</td>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{`${consumerStreetNumber} ${consumerStreet}`}
							</Typography>
						</td>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{`${consumerCity}, ${consumerState}`}
							</Typography>
						</td>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{`${consumerZip}`}
							</Typography>
						</td>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{totalOrderCount}
							</Typography>
						</td>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{formatMoney(averageOrderSubtotal)}
							</Typography>
						</td>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{formatMoney(aggregateOrderSubtotal)}
							</Typography>
						</td>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{formatDateLong(firstOrderInRange)}
							</Typography>
						</td>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{formatDateLong(lastOrderInRange)}
							</Typography>
						</td>
					</tr>
				);
			})}
		</tbody>
	);
};

export default CustomerDataTable;
